import { getEnv } from '../env';

import { getAccessToken, handleUnauthorized } from './axios';

export interface TemplateAiEditRequest {
  instruction: string;
  currentContent: string;
  conversationHistory: Array<{ role: 'user' | 'model'; content: string }>;
  availableVariables: Array<{ path: string; description: string }>;
  editorContext: 'prompt' | 'workflow_email' | 'workflow_sms' | 'workflow_json';
  model: string;
}

export interface TemplateAiSSEEvent {
  type: 'thinking' | 'chunk' | 'result' | 'error';
  content: string;
}

export async function streamTemplateEdit(
  dto: TemplateAiEditRequest,
  onEvent: (event: TemplateAiSSEEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const baseUrl = getEnv('VITE_API_URL');
  const token = getAccessToken();

  const response = await fetch(`${baseUrl}/template-ai/edit-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(dto),
    signal,
  });

  if (response.status === 401) {
    const refreshed = await handleUnauthorized();
    if (refreshed) {
      return streamTemplateEdit(dto, onEvent, signal);
    }
    throw new Error('Session expired. Please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error((errorData as { message?: string }).message || `HTTP error ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const dataStr = line.slice(6).trim();
        if (dataStr) {
          try {
            const event = JSON.parse(dataStr) as TemplateAiSSEEvent;
            onEvent(event);
          } catch {
            // skip malformed SSE lines
          }
        }
      }
    }
  }
}
